import React, { useState, useEffect, useRef } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Box, IconButton, Typography, Paper } from "@mui/material";
import {
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  OpenWith as PanToolIcon,
  ContentCutOutlined,
} from "@mui/icons-material";
import { useHistory } from "react-router-dom";

// Custom hook for handling pan behavior
const usePan = () => {
  const [isPanMode, setIsPanMode] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const [startPoint, setStartPoint] = useState({ x: 0, y: 0 });
  const [scrollPos, setScrollPos] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);

  const handleMouseDown = (e) => {
    if (!isPanMode) return;
    setIsPanning(true);
    setStartPoint({
      x: e.pageX - containerRef.current.offsetLeft,
      y: e.pageY - containerRef.current.offsetTop,
    });
    setScrollPos({
      x: containerRef.current.scrollLeft,
      y: containerRef.current.scrollTop,
    });
  };

  const handleMouseMove = (e) => {
    if (!isPanning) return;

    e.preventDefault();
    const x = e.pageX - containerRef.current.offsetLeft;
    const y = e.pageY - containerRef.current.offsetTop;

    const deltaX = x - startPoint.x;
    const deltaY = y - startPoint.y;

    containerRef.current.scrollLeft = scrollPos.x - deltaX;
    containerRef.current.scrollTop = scrollPos.y - deltaY;
  };

  const handleMouseUp = () => {
    setIsPanning(false);
  };

  const togglePanMode = () => {
    setIsPanMode(!isPanMode);
  };

  return {
    containerRef,
    isPanning,
    isPanMode,
    togglePanMode,
    handlers: {
      onMouseDown: handleMouseDown,
      onMouseMove: handleMouseMove,
      onMouseUp: handleMouseUp,
      onMouseLeave: handleMouseUp,
    },
  };
};

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

export default function PdfHighlighterPanZoom({
  pdfBlob,
  extractedData,
  hoveredFieldKey,
  pinnedHighlights,
  containerWidth = 750,
  containerHeight = 700,
  activePage,
  scrollOnPageChange,
  onScrolled,
  pdf,
}) {
  const [numPages, setNumPages] = useState(null);
  const [pageBaseDims, setPageBaseDims] = useState({});
  const [highlights, setHighlights] = useState([]);
  const ZOOM_LEVEL = 1.35;
  const [zoom, setZoom] = useState(ZOOM_LEVEL);

  const history = useHistory();

  const viewRecord = (row) => {
    history.push({
      pathname: "/viewpage",
      state: { pdfVal: row, indexPageState: 0 },
    });
  };

  // Initialize pan functionality
  const { containerRef, handlers, isPanning, isPanMode, togglePanMode } =
    usePan();

  function extractAllCoordinates(data) {
    if (!data?.coordinates) return [];
    return Object.entries(data.coordinates).map(([fieldKey, coordInfo]) => ({
      fieldKey,
      page: coordInfo.page,
      boundingBox: coordInfo.boundingBox,
    }));
  }

  useEffect(() => {
    if (extractedData) {
      const found = extractAllCoordinates(extractedData);
      setHighlights(found);
    }
  }, [extractedData]);

  function onDocumentLoadSuccess(pdf) {
    setNumPages(pdf.numPages);
  }

  function onPageRenderSuccess(page, pageNumber) {
    setPageBaseDims((prev) => ({
      ...prev,
      [pageNumber]: {
        width: page.width,
        height: page.height,
      },
    }));
  }

  const zoomIn = () => setZoom((z) => Math.min(z + 0.25, 8.0));
  const zoomOut = () => setZoom((z) => Math.max(z - 0.25, 0.25));

  if (!pdfBlob) {
    return <div>No PDF provided</div>;
  }

  return (
    <Box sx={{ backgroundColor: "white" }}>
      <Paper
        elevation={0}
        sx={{
          mb: 2,
          p: 1,
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderRadius: 2,
          justifyContent: "center",
        }}
      >
        <IconButton
          size="small"
          onClick={zoomOut}
          sx={{
            color: "primary.main",
            "&:hover": {
              backgroundColor: "rgba(72, 73, 232, 0.08)",
            },
          }}
        >
          <ZoomOutIcon fontSize="small" />
        </IconButton>

        <Typography variant="body2" color="text.secondary">
          {zoom.toFixed(2)}x
        </Typography>

        <IconButton
          size="small"
          onClick={zoomIn}
          sx={{
            color: "primary.main",
            "&:hover": {
              backgroundColor: "rgba(72, 73, 232, 0.08)",
            },
          }}
        >
          <ZoomInIcon fontSize="small" />
        </IconButton>

        <IconButton
          size="small"
          onClick={togglePanMode}
          sx={{
            color: isPanMode ? "primary.main" : "text.secondary",
            backgroundColor: isPanMode
              ? "rgba(72, 73, 232, 0.08)"
              : "transparent",
            "&:hover": {
              backgroundColor: "rgba(72, 73, 232, 0.08)",
            },
          }}
        >
          <PanToolIcon fontSize="small" />
        </IconButton>

        <IconButton
          size="small"
          onClick={() => viewRecord(pdf)}
          sx={{
            color: "text.secondary",
            backgroundColor: "transparent",
            "&:hover": {
              backgroundColor: "rgba(72, 73, 232, 0.08)",
            },
          }}
        >
          <ContentCutOutlined fontSize="small" />
        </IconButton>
      </Paper>

      <Box
        ref={containerRef}
        {...handlers}
        sx={{
          width: containerWidth,
          height: containerHeight,
          overflow: "auto",
          border: "1px solid #e0e0e0",
          borderRadius: 1,
          position: "relative",
          bgcolor: "background.paper",
          cursor: isPanning ? "grabbing" : isPanMode ? "grab" : "default",
        }}
      >
        <Document
          file={pdfBlob}
          onLoadSuccess={onDocumentLoadSuccess}
          loading={
            <Box
              sx={{
                p: 2,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <Typography color="text.secondary">Loading PDF...</Typography>
            </Box>
          }
        >
          {Array.from(new Array(numPages), (el, i) => {
            const pageNum = i + 1;
            return (
              <PageMeasurer
                key={`page_meas_${pageNum}`}
                pageNum={pageNum}
                onPageRenderSuccess={onPageRenderSuccess}
              />
            );
          })}

          {Array.from(new Array(numPages), (el, i) => {
            const pageNum = i + 1;
            const baseDims = pageBaseDims[pageNum];
            if (!baseDims) return null;

            return (
              <Box
                key={`page_view_${pageNum}`}
                sx={{
                  position: "relative",
                  width: baseDims.width * zoom,
                  height: baseDims.height * zoom,
                  mb: 2.5,
                }}
                ref={(el) => {
                  if (el && pageNum === activePage && scrollOnPageChange) {
                    el.scrollIntoView({ behavior: "smooth" });
                    onScrolled();
                  }
                }}
              >
                <Box
                  sx={{
                    transform: `scale(${zoom})`,
                    transformOrigin: "top left",
                    width: baseDims.width,
                    height: baseDims.height,
                    position: "relative",
                  }}
                >
                  <PageViewer
                    pageNum={pageNum}
                    width={baseDims.width}
                    height={baseDims.height}
                  />
                  <HighlightOverlay
                    pageNum={pageNum}
                    highlights={highlights}
                    baseDims={baseDims}
                    hoveredFieldKey={hoveredFieldKey}
                    pinnedHighlights={pinnedHighlights}
                  />
                </Box>
              </Box>
            );
          })}
        </Document>
      </Box>
    </Box>
  );
}

function PageMeasurer({ pageNum, onPageRenderSuccess }) {
  return (
    <div style={{ width: 0, height: 0 }}>
      <Page
        pageNumber={pageNum}
        scale={1}
        renderAnnotationLayer={false}
        renderTextLayer={false}
        onRenderSuccess={(page) => onPageRenderSuccess(page, pageNum)}
      />
    </div>
  );
}

function PageViewer({ pageNum, width, height }) {
  return (
    <Page
      pageNumber={pageNum}
      scale={1}
      renderAnnotationLayer={false}
      renderTextLayer={false}
      width={width}
      style={{ position: "absolute", top: 0, left: 0 }}
    />
  );
}

function HighlightOverlay({
  pageNum,
  highlights,
  baseDims,
  hoveredFieldKey,
  pinnedHighlights,
}) {
  const boxesForPage = highlights.filter((h) => h.page === pageNum);
  const { width: baseWidth, height: baseHeight } = baseDims;

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: baseDims.width,
        height: baseDims.height,
      }}
    >
      {boxesForPage.map((h, idx) => {
        const isHovered = h.fieldKey === hoveredFieldKey;
        const isPinned = pinnedHighlights.has(h.fieldKey);
        const shouldShow = isHovered || isPinned;

        if (!shouldShow) {
          return null;
        }

        let { Left, Top, Width, Height } = h.boundingBox;

        const pad = 0.01;
        Left -= pad / 2;
        Top -= pad / 2;
        Width += pad;
        Height += pad;

        if (Left < 0) {
          Width += Left;
          Left = 0;
        }
        if (Top < 0) {
          Height += Top;
          Top = 0;
        }
        if (Left + Width > 1) {
          Width = 1 - Left;
        }
        if (Top + Height > 1) {
          Height = 1 - Top;
        }

        const leftPx = Left * baseWidth;
        const topPx = Top * baseHeight;
        const widthPx = Width * baseWidth;
        const heightPx = Height * baseHeight;
        return (
          <div
            key={idx}
            style={{
              position: "absolute",
              left: leftPx,
              top: topPx,
              width: widthPx,
              height: heightPx,
              backgroundColor: "rgba(225, 225, 59, 0.3)",
              border: "2px solid #FCD34D",
              pointerEvents: "none",
            }}
          />
        );
      })}
    </div>
  );
}
